import { phpAuthUser } from "../core/php-data-access";
import commonUtil from "../util/common-utils";
import { UserModalApi } from "./modal/user-modal-api";

type UnknownRecord = Record<string, unknown>;

export interface UserDisplayItem {
    id: string;
    displayName: string;
    reviewCount: number;
    followersCount: number;
    isFollowing: boolean;
}

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

export class UserDisplayHelper {
    private static followingIdsPromise: Promise<Set<string>> | null = null;

    public static resolveId(raw: unknown): string {
        const obj = isRecord(raw) ? raw : null;
        if (!obj) return "";

        return String(obj.id ?? obj.user_id ?? "").trim();
    }

    public static resolveDisplayName(raw: unknown): string {
        const obj = isRecord(raw) ? raw : null;
        if (!obj) return "User";

        const value = String(
            obj.display_name ??
                obj.nick_name ??
                obj.full_name ??
                obj.name ??
                obj.email ??
                obj.id ??
                obj.user_id ??
                "User",
        ).trim();

        return value || "User";
    }

    public static ensureAtPrefix(displayName: string): string {
        const normalized = String(displayName || "").trim();
        if (!normalized) {
            return "@User";
        }

        return normalized.startsWith("@") ? normalized : `@${normalized}`;
    }

    public static formatAtDisplayName(raw: unknown): string {
        return this.ensureAtPrefix(this.resolveDisplayName(raw));
    }

    public static resolveReviewCount(raw: unknown): number {
        const obj = isRecord(raw) ? raw : null;
        const value = Number(obj?.review_count ?? obj?.reviews_count ?? obj?.posts_count ?? 0);
        return Number.isFinite(value) ? value : 0;
    }

    public static resolveFollowersCount(raw: unknown): number {
        const obj = isRecord(raw) ? raw : null;
        const value = Number(obj?.followers_count ?? 0);
        return Number.isFinite(value) ? value : 0;
    }

    public static async normalizeUserItem(raw: unknown): Promise<UserDisplayItem | null> {
        const id = this.resolveId(raw);
        if (!id) {
            return null;
        }

        const followingIds = await this.getCurrentFollowingIds();

        return {
            id,
            displayName: this.formatAtDisplayName(raw),
            reviewCount: this.resolveReviewCount(raw),
            followersCount: this.resolveFollowersCount(raw),
            isFollowing: followingIds.has(id),
        };
    }

    public static buildUserMetaHtml(user: Pick<UserDisplayItem, "reviewCount" | "followersCount">): string {
        return `(<i class="bi bi-chat-left-text"></i> ${commonUtil.escapeHtml(String(user.reviewCount))} <i class="bi bi-people"></i> ${commonUtil.escapeHtml(String(user.followersCount))})`;
    }

    public static buildMentionAnchorHtml(user: Pick<UserDisplayItem, "id" | "displayName" | "isFollowing">): string {
        const id = commonUtil.escapeHtmlAttribute(String(user.id || ""));
        const label = commonUtil.escapeHtml(this.ensureAtPrefix(user.displayName));
        const stateClass = user.isFollowing ? "user-mention-link--following" : "user-mention-link--muted";

        return `<a href="/users/${id}" class="user-mention-link ${stateClass}" data-user-id="${id}">${label}</a>`;
    }

    public static async getCurrentFollowingIds(): Promise<Set<string>> {
        if (this.followingIdsPromise) {
            return this.followingIdsPromise;
        }

        this.followingIdsPromise = (async () => {
            const authUser = phpAuthUser();
            const currentUserId = Number(authUser?.user_id ?? authUser?.id ?? 0);
            if (!Number.isInteger(currentUserId) || currentUserId <= 0) {
                return new Set<string>();
            }

            try {
                const followings = await UserModalApi.getFollowings(currentUserId, { page: 1, perPage: 500 });
                return new Set(
                    followings
                        .map((user) =>
                            String((user as UnknownRecord)?.user_id ?? (user as UnknownRecord)?.id ?? "").trim(),
                        )
                        .filter(Boolean),
                );
            } catch (_error) {
                return new Set<string>();
            }
        })();

        return this.followingIdsPromise;
    }
}
